import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
} from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ValidationErrors } from '@angular/forms';
import { ConfirmationTimeRecordingP40Model, TimeRecordingEntryP40Model } from '@evorait/pace-core';
import { faPencilAlt } from '@fortawesome/free-solid-svg-icons';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Subscription } from 'rxjs';
import { take } from 'rxjs/operators';
import { AddTimeRecordingEntryP40Component } from './add-time-recording-entry-p40/add-time-recording-entry-p40.component';

@Component({
    selector: 'pace-confirmation-time-recording-p40',
    templateUrl: './confirmation-time-recording-p40.component.html',
    styleUrls: ['./confirmation-time-recording-p40.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConfirmationTimeRecordingP40Component implements OnInit, OnDestroy {
    @Input()
    public model: ConfirmationTimeRecordingP40Model;

    @Input()
    public parentForm: FormArray;

    @Output()
    public savedTimeRecording = new EventEmitter<void>();

    public faPencil = faPencilAlt;
    public timeRecordingFormChildren: FormArray;
    public timeRecordingForm: FormGroup;
    private editModalSubscription?: Subscription;
    private addModalSubscription?: Subscription;
    public constructor(
        private readonly modalService: BsModalService,
        private readonly formBuilder: FormBuilder,
        private readonly changeDetector: ChangeDetectorRef,
    ) {}

    public ngOnInit(): void {
        const timeRecordingValidator = (): ValidationErrors | null => this.checkValidity();
        this.timeRecordingFormChildren = this.formBuilder.array([]);
        this.timeRecordingForm = this.formBuilder.group(
            {
                children: this.timeRecordingFormChildren,
            },

            {
                validators: timeRecordingValidator,
            },
        );

        this.parentForm.push(this.timeRecordingForm);
    }

    public ngOnDestroy(): void {
        if (typeof this.editModalSubscription !== 'undefined') {
            this.editModalSubscription.unsubscribe();
        }
        if (typeof this.addModalSubscription !== 'undefined') {
            this.addModalSubscription.unsubscribe();
        }
    }

    public checkValidity(): ValidationErrors | null {
        const errors: ValidationErrors = {};

        if (this.model.timeRecordingEntries.value.length < 1) {
            errors.required = { required: 'At least one time recording entry is required' };
        }

        return Object.keys(errors).length > 0 ? errors : null;
    }

    public editTimeRecording(index: number): void {
        const modalInstance = this.modalService.show(AddTimeRecordingEntryP40Component, {
            backdrop: 'static',
            class: 'modal-md',
            initialState: {
                entry: this.model.timeRecordingEntries.value[index],
                edit: true,
            },
            ariaLabelledBy: 'add-time-recording-entry-p40-modal-title',
        });

        if (typeof this.editModalSubscription !== 'undefined') {
            this.editModalSubscription.unsubscribe();
        }
        this.editModalSubscription = modalInstance.content?.entryChange
            .pipe(take(1))
            .subscribe((entry: TimeRecordingEntryP40Model) => {
                this.model.timeRecordingEntries.value[index] = entry;
                this.model.sortTimeRecordingEntries();
                this.timeRecordingForm.updateValueAndValidity();
                this.changeDetector.markForCheck();
                this.savedTimeRecording.emit();
            });
    }

    public deleteTimeRecording(index: number): void {
        this.model.timeRecordingEntries.removeModel(index);
        this.timeRecordingForm.updateValueAndValidity();
        this.changeDetector.markForCheck();
        this.savedTimeRecording.emit();
    }

    public addTimeRecording(): void {
        const newEntry = new TimeRecordingEntryP40Model(this.model.deliveryType, this.model.division);

        const modalInstance = this.modalService.show(AddTimeRecordingEntryP40Component, {
            backdrop: 'static',
            class: 'modal-md',
            initialState: {
                entry: newEntry,
                edit: false,
            },
            ariaLabelledBy: 'add-time-recording-entry-p40-modal-title',
        });

        if (typeof this.addModalSubscription !== 'undefined') {
            this.addModalSubscription.unsubscribe();
        }
        this.addModalSubscription = modalInstance.content?.entryChange
            .pipe(take(1))
            .subscribe((entry: TimeRecordingEntryP40Model) => {
                this.model.timeRecordingEntries.value.push(entry);
                this.model.sortTimeRecordingEntries();
                this.timeRecordingForm.updateValueAndValidity();
                this.changeDetector.markForCheck();
                this.savedTimeRecording.emit();
            });
    }
}
